import { useEffect, useState } from "react";

/**
 * Subscribes to a CSS media query and re-renders on change.
 * Guarded for SSR / non-DOM environments (returns `false`).
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    if (mq.addEventListener) {
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }, [query]);

  return matches;
}

/**
 * Breakpoints (mobile-first). Aligned with the mockup's spatial rules:
 *   - Desktop: TopNav + Grid + floating ContextPanel + HUD
 *   - Tablet:  Grid dominant, ContextPanel may collapse
 *   - Mobile:  Grid + BottomSheet
 */
export const useIsDesktop = () => useMediaQuery("(min-width: 1024px)");
export const useIsTablet = () =>
  useMediaQuery("(min-width: 768px) and (max-width: 1023.98px)");
export const useIsMobile = () => useMediaQuery("(max-width: 767.98px)");
